import { runBatch } from '../../../utils/office/document';
import { anchorAt, findNumberSourceParagraph } from './crossReferences';

/** Zero-width space used as a throwaway insertion anchor. Named rather than
 *  written literally — an invisible character in source is unreadable. */
const ZERO_WIDTH = '\u200B';

/** XML-escapes text going into an OOXML element. */
function xmlEscape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

/** One `w:instrText` run of field-code text with explicit bold/underline. */
function instrRun(text: string, bold: boolean): string {
  // Both properties are stated explicitly, never left to inherit: the run sits
  // between bold+underlined neighbours, and an omitted w:b would pick theirs up.
  const rPr = `<w:rPr><w:b w:val="${bold ? '1' : '0'}"/><w:u w:val="none"/></w:rPr>`;
  return `<w:r>${rPr}<w:instrText xml:space="preserve">${xmlEscape(text)}</w:instrText></w:r>`;
}

/**
 * The TC field as a flat-OPC OOXML package:
 *
 *     {TC "Exhibit {REF _V14x \h \w}:<TAB>Name" \f a \l 5}
 *
 * with `Exhibit ` and the nested REF bold, and `TC "` / `:` onwards plain.
 *
 * The REF is a real nested field, not the number as text — so the generated
 * exhibit table follows a renumbering of the contract instead of freezing
 * whatever the number was at insertion time.
 *
 * `placeholderNumber` is the nested field's result — what Word shows until it
 * next updates fields, replaced on update rather than stored as the value.
 *
 * Pure and exported for testing — the run/rPr/nesting structure is the whole
 * point of doing this in OOXML and is not observable any other way.
 *
 * The tab between label and name is the numeric entity `&#9;`: a raw tab in the
 * source XML is liable to be normalised away, and the exhibit table needs a real
 * tab to align its columns.
 */
export function buildTcFieldOoxml(
  exhibitType: string,
  refCode: string,
  placeholderNumber: string,
  description: string,
): string {
  // Word's own shape for a field nested in a field CODE (verified against a
  // hand-built entry): begin / instrText / separate / result / end — and the
  // result is an `instrText` run too, NOT `w:t`, because everything between the
  // outer field's begin and end is field code. Emitting `w:t` there produced a
  // structure Word discarded; omitting the result region left the number blank.
  const nestedRef =
    `<w:r><w:fldChar w:fldCharType="begin"/></w:r>` +
    instrRun(refCode, true) +
    `<w:r><w:fldChar w:fldCharType="separate"/></w:r>` +
    instrRun(placeholderNumber, true) +
    `<w:r><w:fldChar w:fldCharType="end"/></w:r>`;

  const runs =
    `<w:r><w:fldChar w:fldCharType="begin"/></w:r>` +
    instrRun('TC "', false) +
    instrRun(`${exhibitType} `, true) +
    nestedRef +
    `<w:r><w:rPr><w:b w:val="0"/><w:u w:val="none"/></w:rPr>` +
    `<w:instrText xml:space="preserve">:&#9;${xmlEscape(description)}" \\f a \\l 5</w:instrText></w:r>` +
    `<w:r><w:fldChar w:fldCharType="end"/></w:r>`;

  return (
    `<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">` +
    `<pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml" pkg:padding="512">` +
    `<pkg:xmlData><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>` +
    `</Relationships></pkg:xmlData></pkg:part>` +
    `<pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">` +
    `<pkg:xmlData><w:document xmlns:w="${W_NS}"><w:body><w:p>${runs}</w:p></w:body></w:document>` +
    `</pkg:xmlData></pkg:part></pkg:package>`
  );
}

/**
 * Inserts a Table of Contents (TOC) field at the current selection.
 */
export async function insertTableOfContents(): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    // Levels 1–3 (was 1 only, which surprised users expecting sub-headings).
    selection.insertField(
      Word.InsertLocation.replace,
      Word.FieldType.empty,
      'TOC \\o "1-3" \\h \\z \\u',
    );
    await context.sync();
  });
}

/**
 * Inserts an Exhibits/Appendix table (TOC filtered by TC field identifier "a").
 */
export async function insertExhibitTable(): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    selection.insertField(
      Word.InsertLocation.replace,
      Word.FieldType.empty,
      'TOC \\f a \\n \\p',
    );
    await context.sync();
  });
}

/**
 * Inserts an exhibit entry numbered from the SECTION it sits in:
 *
 *     Exhibit {REF _V14x \\h \\w}{TC "Exhibit {REF _V14x \\h \\w}:<TAB>Name" \\f a \\l 5}
 *
 * rendered as `Exhibit 2.1{TC "Exhibit 2.1:<TAB>Name" …}`.
 *
 * - The number is the **current section's** number, not a running counter: insert
 *   in section 2.1 and you get "Exhibit 2.1". If the paragraph itself carries no
 *   number, the closest PRECEDING numbered paragraph supplies it.
 * - It is a REF field to a hidden anchor on that paragraph, so renumbering the
 *   contract updates the exhibit too. `\w` renders the FULL CONTEXT number
 *   ("2.1(a)", not "(a)") and `\h` makes it a hyperlink. Full context is the
 *   house standard (confirmed 2026-08-05) — do not "fix" this to `\r`, which
 *   yields the bare number and is what a hand-built reference tends to use.
 * - The number inside the TC field is the SAME REF field, nested in the field
 *   code — so the generated exhibit table also follows a renumbering instead of
 *   freezing the number it had when inserted.
 * - The exhibit's NAME lives only inside the TC field, which is what the
 *   generated table (`TOC \f a`) reads. Nothing after the number is visible.
 *
 * Inside the field code the formatting is explicit, because it carries into the
 * generated table the same way a bold XE entry bleeds into an index:
 *   `TC "` and `:` onwards → neither bold nor underlined
 *   `Exhibit ` + the nested REF → bold, NOT underlined
 * It is built as OOXML (`buildTcFieldOoxml`): `Word.Field` exposes `code` as a
 * plain string and a Range only for `result`, so a field code cannot be formatted
 * — or given a nested field — through the field API at all.
 */
export async function insertExhibit(
  exhibitType: string,
  description: string,
): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    const paragraphs = selection.paragraphs;
    paragraphs.load('items');
    await context.sync();

    if (paragraphs.items.length === 0) {
      throw new Error('No paragraph found at the current position.');
    }

    // The current paragraph when numbered, else the closest preceding numbered
    // one (shared with the self-reference — same notion of "current section").
    const source = await findNumberSourceParagraph(context, paragraphs.items[0]);
    if (!source) {
      throw new Error(
        'No numbered section found at or before the cursor — an exhibit takes its number from its section.',
      );
    }

    // Anchor the numbering paragraph (reuses one of ours if already there) and
    // point a REF at it, exactly like a cross-reference.
    const anchorName = await anchorAt(context, source.paragraph.getRange(Word.RangeLocation.start));
    const refCode = `REF ${anchorName} \\h \\w`;

    // Visible label + number, bold and underlined.
    const labelRange = selection.insertText(`${exhibitType} `, Word.InsertLocation.replace);
    labelRange.font.bold = true;
    labelRange.font.underline = Word.UnderlineType.single;

    // A sentinel placed BEFORE the REF field exists, so it ends up immediately
    // AFTER it — the TC field has to land outside the REF, past its `end`.
    //
    // `refField.result` is the region INSIDE the field, between `separate` and
    // `end`. Inserting the TC field there (which is what "after the result"
    // means) nested it inside the REF's result, and F9 regenerates a field's
    // result — deleting the TC field with it. Verified in the OOXML of a document
    // where the same entry was also built by hand: the hand-built TC sits after
    // the REF's `end`, ours sat inside it.
    const sentinel = labelRange.insertText(ZERO_WIDTH, Word.InsertLocation.after);
    const refField = labelRange.insertField(
      Word.InsertLocation.after,
      Word.FieldType.empty,
      refCode,
    );
    refField.load('result');
    await context.sync();

    refField.result.font.bold = true;
    refField.result.font.underline = Word.UnderlineType.single;
    // Read back what the REF RENDERED for the nested field's placeholder: `\w`
    // gives the full context number ("2.1(a)"), which the paragraph's own
    // listString does not — a sub-item's listString is just "(a)".
    refField.result.load('text');
    await context.sync();

    const renderedNumber = (refField.result.text ?? '').trim();
    // A quote inside the name would close the TC argument early.
    const safeDescription = description.replace(/"/g, "'");

    // Replacing the sentinel consumes the zero-width character, so nothing is
    // left behind if the insert succeeds.
    sentinel.insertOoxml(
      buildTcFieldOoxml(exhibitType, refCode, renderedNumber, safeDescription),
      Word.InsertLocation.replace,
    );

    await context.sync();
  });
}
